const { RequestContext } = require('@fiverr-private/perseus');
const { getShortLanguageCode } = require('@fiverr-private/localization');
const pathfinder = require('@fiverr-private/pathfinder');
const { RichTextHeroMedia } = require('@fiverr-private/cms_ui');

const {
    CMS_PACKAGE_CONTEXT,
    ROOT_CONTENT_TYPES,
    SLIDER_BREAKPOINTS,
    PAGE_TYPES
} = require('../constants');
const { carouselQueryParamEnrichments, linksQueryParamEnrichments } = require('../utils/biEnrichments');
const { convertSlugToText } = require('./common');

const PAGE_TYPE_CONFIG = {
    [PAGE_TYPES.CONTENT_LANDING_PAGE]: {
        getCmsRequest: (req) => {
            const requestContext = new RequestContext(req);
            const {
                article_id,
                perseus_name,
                app_base_path
            } = requestContext.pathParameters;
            const {
                gig_id,
                order_id
            } = requestContext.queryParameters;
            const { userId, pageCtxId, locale, countryCode } = requestContext;
            const url = new URL(requestContext.url);
            const slug = url.pathname.startsWith('/content/rt/') ? `content/rt/${article_id}` : `content/${article_id}`;
            return {
                useCmsService: true,
                locale,
                slug,
                contentType: ROOT_CONTENT_TYPES.CONTENT_LANDING_PAGE,
                additionalProps: {
                    context: {

                        // Used by cms-ui ->  GigsCarouselCatalog && GigsCarouselSearchQuery && GigsCarouselRecommendationRoute
                        // src/content_types/GigsCarouselSearchQuery/GigsCarouselSearchQuery.js
                        // src/content_types/GigsCarouselCatalog/GigsCarouselCatalog.js
                        // src/content_types/GigsCarouselRecommendationRoute/GigsCarouselRecommendationRoute.js
                        countryCode,

                        // Used by cms-ui -> gigsCarousel & GigsCarouselRecommendationRoute
                        // src/content_types/GigsCarousel/component/GigsCarousel.js
                        // src/content_types/GigsCarouselRecommendationRoute/GigsCarouselRecommendationRoute.js
                        userId,

                        // Used by cms-ui -> gigsCarousel
                        // src/content_types/GigsCarousel/component/GigsCarousel.js
                        pageCtxId,

                        // Used by cms-ui, passed via cmsProvider
                        // method: useCSR
                        // src/content_types/CMSProvider.js
                        params: {
                            perseus_name,
                            app_base_path
                        }
                    },
                    ...carouselQueryParamEnrichments({ article_id }),
                    ...linksQueryParamEnrichments({ article_id }),
                    impressionEnrichment: {
                        sourceComponent: '',
                        sourcePage: `content_landing_page_${article_id}`,
                        ...CMS_PACKAGE_CONTEXT.impressionEnrichment
                    },
                    maxStaticItems: CMS_PACKAGE_CONTEXT.maxStaticItems,
                    sliderBaseNum: {
                        GigCardCarousel: 3,
                        GigCardListings: 3,
                        SmallGigCardCarousel: 5
                    },
                    originUrl: req.headers['origin-url'],
                    sliderBreakpoints: SLIDER_BREAKPOINTS,

                    // Used by cms_ui GigsCarouselRecommendationRoute
                    gig_id,

                    // Used by cms_ui GigsCarouselRecommendationRoute
                    order_id
                }
            };
        },
        breadcrumbs: {
            generateBreadcrumbs: false
        },
        getPathHierarchy: ({ articleId }) => [articleId],
        getMixpanelPageViewEvent: (data) => ({
            pageName: 'Content Landing Page',
            payload: {
                'Article Name': data.slug
            }
        }),
        getBigqueryPageViewEvent: (context, data) => ({
            type: 'Article',
            group: 'content_landing_pages',
            platform: 'web',
            user: { id: context.userId },
            page: { name: 'Content Landing Page', ctx_id: context.pageCtxId },
            domain: { name: 'content_landing_pages' },
            article: { name: data.slug }
        })
    },
    [PAGE_TYPES.SELLER_JOB_PAGE]: {
        getCmsRequest: (req) => {
            const requestContext = new RequestContext(req);
            const {
                hub_id,
                category_id,
                job_id
            } = requestContext.pathParameters;
            const { locale } = requestContext;

            let slug = `pages/${getShortLanguageCode(locale)}-${hub_id}`;

            if (category_id) {
                slug += `-${category_id}`;
            }
            if (job_id) {
                slug += `-${job_id}`;
            }

            return {
                slug,
                contentType: ROOT_CONTENT_TYPES.CONTENT_LANDING_PAGE
            };
        },
        headerBannerComponent: RichTextHeroMedia,
        breadcrumbs: {
            generateBreadcrumbs: true,
            includeSeoScript: true,
            pathPrefix: 'sellers'
        },
        getPathHierarchy: ({ hubId, categoryId, jobId }) => {
            const hierarchy = [hubId];

            if (categoryId) {
                hierarchy.push(categoryId);
            }

            if (jobId) {
                hierarchy.push(jobId);
            }

            return hierarchy;
        },
        getMixpanelPageViewEvent: (data) => {
            const pathHierarchy = data.pageInfo.pathHierarchy;
            const pageName = convertSlugToText(pathHierarchy[pathHierarchy.length - 1]);
            const levelMapping = {
                1: 'hub page',
                2: 'category page',
                3: 'job page'
            };
            const source = new URL(window.location).searchParams.get('source');

            return {
                pageName: 'Seller Job Page',
                payload: {
                    'Page name': pageName,
                    'Page level': levelMapping[pathHierarchy.length],
                    'Page type': pageName,
                    'Source': source
                }
            };
        },
        getBigqueryPageViewEvent: (context, data) => {
            const pathHierarchy = data.pageInfo.pathHierarchy;
            const pageName = convertSlugToText(pathHierarchy[pathHierarchy.length - 1]);
            const levelMapping = {
                1: 'hub_page',
                2: 'category_page',
                3: 'job_page'
            };
            const pageLevel = levelMapping[pathHierarchy.length];
            const source = new URL(window.location).searchParams.get('source');

            return {
                type: `seller_seo_${pageLevel}_is_shown`,
                group: 'page_view',
                platform: 'web',
                user: { id: context.userId },
                page: {
                    name: `seller_seo_${pageLevel}`,
                    ctx_id: context.pageCtxId,
                    referrer: {
                        name: source
                    }
                },
                action: { type: 'page_view' },
                feature: { name: 'seller_acquisition_seo' },
                element: { type: pageName, name: pageName, metadata: pageName },
                domain: { name: 'localization' }
            };
        },
        redirectInvalidRequest: (language, pageParams) => {
            let redirectPath;
            let shouldRedirect = false;

            const validHubEntries = {
                es: 'trabajos-freelance',
                de: 'freelance-jobs',
                fr: 'missions-freelance'
            };

            if (!validHubEntries[language]) {
                redirectPath = pathfinder('start_selling');
                shouldRedirect = true;
            } else if (validHubEntries[language] !== pageParams.hubId) {
                redirectPath = `/sellers/${validHubEntries[language]}`;
                shouldRedirect = true;
            }

            return {
                redirectPath,
                shouldRedirect
            };
        },
        addSourceToAnchorElements: (pathHierarchy) => {
            const landingPageElement = document.getElementsByClassName('landing-page')[0];
            const anchorElements = landingPageElement.getElementsByTagName('a');

            for (let i = 0; i < anchorElements.length; i++) {
                const url = new URL(anchorElements[i].href);
                const searchParams = url.searchParams;
                searchParams.set('source', pathHierarchy[pathHierarchy.length - 1]);
                url.search = searchParams.toString();
                anchorElements[i].href = url.toString();
            }
        }
    }
};

module.exports = {
    PAGE_TYPES,
    PAGE_TYPE_CONFIG
};
